import React, { useState, useEffect, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { format } from 'date-fns';
import { toast } from 'react-toastify';
import { uniqueId } from 'lodash';
import { filesize } from 'filesize';
import { FiTrash2 } from 'react-icons/fi';

import api from 'services/api';
import { show } from 'store/modules/dialog/actions';
import ListImage from 'assets/available.svg';
import { PlanilhaValidos } from 'pages/@types';
import AppError from 'utils/AppError';

import Upload from 'components/Upload';
import Button from 'components/Button';
import LoadingTable from 'components/LoadingTable';
import Flag from 'components/Flag';
import NotFound from 'components/NotFound';
import Avatar from 'components/Avatar';

import { Card } from './styles';
import { Scroll, Table } from 'styles/global';
import { StatusPlanilhaValidos } from 'utils/variables';

export default function Validos() {
  const { callback: callbackDialog } = useSelector(
    (state: any) => state.dialog,
  );
  const dispatch = useDispatch();
  const [selectedFilesValidos, setSelectedFilesValidos] = useState<any[]>([]);
  const [selected, setSelectedValidos] = useState({} as PlanilhaValidos);
  const [loading, setLoading] = useState(true);
  const [planilhasValidos, setPlanilhasValidos] = useState<PlanilhaValidos[]>(
    [],
  );

  const handleArquivaValidos = async (p: PlanilhaValidos) => {
    setSelectedValidos(p);
    try {
      const option = {
        type: 'question',
        text: `Deseja deletar ${p.originalfilename}?`,
        confirmButtonText: 'CONFIRMAR',
        callback: {
          url: `/planilhasvalidos/${p.id}`,
          method: api.delete,
        },
        haveCallback: true,
        successMessage: 'Planilha deletada!',
        cancelButtonText: 'NÃO',
        showCancelButton: false,
        showConfirmButton: true,
        showCloseButton: true,
      };
      dispatch(show(option));
    } catch (err) {}
  };

  const handleSuccessMessage = useCallback(() => {
    const option = {
      type: 'success',
      confirmButtonText: '',
      contentHTML: 'Planilha carregada',
      timer: 3000,
      cancelButtonText: 'NÃO',
      showCancelButton: false,
      showConfirmButton: false,
      showCloseButton: true,
    };
    dispatch(show(option));
  }, [dispatch]);

  const handleErrorMessage = useCallback(
    message => {
      const option = {
        type: 'error',
        confirmButtonText: '',
        text: message,
        timer: 3000,
        cancelButtonText: 'NÃO',
        showCancelButton: false,
        showConfirmButton: false,
        showCloseButton: true,
      };
      dispatch(show(option));
    },
    [dispatch],
  );

  const updateFileValidos = useCallback(
    (id, data) => {
      const files = selectedFilesValidos.map(uploadedFileValidos => {
        return id === uploadedFileValidos.id
          ? { ...uploadedFileValidos, ...data }
          : uploadedFileValidos;
      });
      setSelectedFilesValidos(files);
    },
    [selectedFilesValidos],
  );

  const handleUploadValidos = useCallback(files => {
    const filesFormatted = files.map((file: any) => ({
      file,
      id: uniqueId(),
      name: file.name,
      readableSize: filesize(file.size),
      progress: 0,
      uploaded: false,
      error: false,
    }));

    setSelectedFilesValidos(filesFormatted);
  }, []);

  const processUploadValidos = useCallback(
    selectedFileValidos => {
      const data = new FormData();

      data.append('file', selectedFileValidos.file);

      api
        .post(`/planilhasvalidos`, data, {
          onUploadProgress: e => {
            const progress = e.total
              ? Math.round((e.loaded * 100) / e.total)
              : 0;
            updateFileValidos(selectedFileValidos.id, {
              progress,
            });
          },
        })
        .then(response => {
          updateFileValidos(selectedFileValidos.id, {
            uploaded: true,
          });
          if (response && response.data)
            setPlanilhasValidos(prevState => prevState.concat(response.data));
          else toast('Falha ao carregar planilha');
          setTimeout(() => setSelectedFilesValidos([]), 2000);
          handleSuccessMessage();
        })
        .catch(err => {
          updateFileValidos(selectedFileValidos.id, {
            error: true,
          });
          setTimeout(() => setSelectedFilesValidos([]), 2000);
          handleErrorMessage(err.message);
        });
    },
    [updateFileValidos, handleSuccessMessage, handleErrorMessage],
  );

  useEffect(
    () =>
      selectedFilesValidos.forEach(selectedFilesValidos => {
        if (
          !selectedFilesValidos.uploaded &&
          !selectedFilesValidos.error &&
          selectedFilesValidos.progress === 0
        ) {
          processUploadValidos(selectedFilesValidos);
        }
      }),
    [processUploadValidos, selectedFilesValidos],
  );

  useEffect(() => {
    const controller = new AbortController();
    async function loadValidos() {
      setLoading(true);
      try {
        const response = await api.get('/planilhasvalidos', {
          signal: controller.signal,
        });
        if (response && response.data) {
          setPlanilhasValidos(response.data);
        } else toast.error('Falha de comunicação com o sistema');
        setLoading(false);
      } catch (error) {
        if (error instanceof Error) toast.error(error.message);
        else {
          const appError = error as AppError;
          toast.error(appError.message);
        }
        setLoading(false);
      }
    }
    loadValidos();
    return () => {
      controller.abort();
    };
  }, []);

  // Remove Planilha de Validos excluída da lista e atualiza status de erro para pendente
  useEffect(() => {
    if (callbackDialog && callbackDialog.success)
      if (callbackDialog.success.action === 'success' && selected) {
        setPlanilhasValidos(prevState =>
          prevState
            .filter(p => p.id !== selected.id)
            .map(p => ({
              ...p,
              StatusPlanilhaValidos:
                p.status === StatusPlanilhaValidos.ERRO
                  ? StatusPlanilhaValidos.PENDENTE
                  : p.status,
            })),
        );
      }
  }, [callbackDialog, selected]);

  return (
    <Card height="full" padding="15px">
      <header>
        <h4>LISTA DE PLANILHAS DE VÁLIDOS</h4>
        <div>
          <Upload
            disabled={false}
            accept={{
              'application/vnd.ms-excel': ['.xls'],
              'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': [
                '.xlsx',
              ],
            }}
            onUpload={handleUploadValidos}
            selectedFiles={selectedFilesValidos}
          />
        </div>
      </header>

      {planilhasValidos.length > 0 && !loading && (
        <Scroll>
          <Table>
            <thead>
              <tr>
                <th>NOME DA PLANILHA</th>
                <th style={{ width: '200px' }}>EXIBIDORA</th>
                <th style={{ width: '200px' }}>STATUS</th>
                <th style={{ width: '150px' }}>USUÁRIO</th>
                <th style={{ width: '170px' }}>CARREGADO EM</th>
                <th style={{ width: '65px' }}>AÇÕES</th>
              </tr>
            </thead>

            <tbody>
              {!!planilhasValidos &&
                planilhasValidos.map(p => (
                  <tr key={p.id}>
                    <td>{p.originalfilename}</td>
                    <td>
                      {p.emissora ? p.emissora.sigla : `NÃO IDENTIFICADA`}
                    </td>
                    <td>
                      <Flag type={p.status} name={p.status} />
                    </td>
                    <td>
                      <Avatar
                        size="40"
                        name={p.usuario?.nome}
                        src={p.usuario?.avatar_url}
                        round
                      />
                    </td>
                    <td>{format(new Date(p.createdAt), 'dd/MM/yyyy HH:mm')}</td>
                    <td>
                      <Button
                        id={`arquiva_${p.id}`}
                        fill
                        color="font"
                        round
                        onClick={() => handleArquivaValidos(p)}
                      >
                        <FiTrash2 />
                      </Button>
                    </td>
                  </tr>
                ))}
            </tbody>
          </Table>
        </Scroll>
      )}

      {planilhasValidos.length <= 0 && !loading && (
        <div>
          <NotFound img={ListImage} title="Válidos">
            Nenhuma planilha <b>carregada</b>
          </NotFound>
        </div>
      )}
      {loading && <LoadingTable type="" />}
    </Card>
  );
}
